
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App.tsx';
import './index.css';
import './lib/i18n';
import { getGitCommitHash } from './utils/gitUtils';
import { measureAsyncOperation, LoadingPhase } from './hooks/useLoadingMetrics';


// Initialize Sentry with performance tracking
const initializeSentry = async () => {
  if (import.meta.env.VITE_SENTRY_DSN) {
    await measureAsyncOperation(
      LoadingPhase.APP_INITIALIZATION,
      async () => {
        Sentry.init({
          dsn: import.meta.env.VITE_SENTRY_DSN,
          environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development',
          release: getGitCommitHash(),
          enableLogs: true,
          integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration(),
            Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
          ],
          // Performance Monitoring
          tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
          // Session Replay
          replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 0.1,
          replaysOnErrorSampleRate: 1.0,
        });
      },
      {
        environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development',
        hasRelease: !!getGitCommitHash()
      }
    );
  }
};

// Initialize Sentry
initializeSentry();

// Service worker detection with Sentry logging
if ('serviceWorker' in navigator) {
  window.addEventListener('load', async () => {
    await measureAsyncOperation(
      LoadingPhase.SERVICE_WORKER_REGISTRATION,
      async () => {
        // The service worker is already registered in firebase-messaging-sw.js
        console.log('Service worker support detected. PWA functionality available.');
        
        // Log whether the app is running in standalone mode (installed as PWA)
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
        if (isStandalone) {
          console.log('App is running as installed PWA');
        }
        
        return { serviceWorkerSupported: true, isStandalone };
      },
      {
        serviceWorkerSupported: true,
        isStandalone: window.matchMedia('(display-mode: standalone)').matches
      }
    );
  });
}

// Initialize React app with performance tracking
measureAsyncOperation(
  LoadingPhase.FIRST_RENDER,
  async () => {
    const root = createRoot(document.getElementById("root")!);
    root.render(<App />);
    return { rendered: true };
  },
  { reactVersion: '18' }
);
